import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shown when the current user's accepted privacy policy version no longer
 * matches the current published version. Non-dismissable until the user
 * either accepts or signs out.
 *
 * The /auth/me endpoint exposes privacy_needs_reaccept as a boolean, so
 * we just listen to changes in the user object and open when needed.
 */
export default function PrivacyReacceptModal() {
  const { user, getAuthHeaders, setUser, logout } = useAuth();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.privacy_needs_reaccept) {
      setOpen(true);
      setAccepted(false);
    } else {
      setOpen(false);
    }
  }, [user?.privacy_needs_reaccept]);

  const lbl = (es, en) => (language === 'es' ? es : en);

  async function submit() {
    if (!accepted) {
      toast.error(lbl('Debe marcar la casilla para aceptar.', 'Please check the box to accept.'));
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/accept-privacy`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Accept failed');
      }
      const data = await res.json();
      if (user) {
        setUser({
          ...user,
          privacy_version_accepted: data.version,
          privacy_needs_reaccept: false,
        });
      }
      setOpen(false);
      toast.success(lbl('Politica aceptada', 'Policy accepted'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    await logout?.();
    setOpen(false);
  }

  // Don't render anything until we have a clear signal
  if (!user?.privacy_needs_reaccept) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissable */ }}>
      <DialogContent
        className="max-w-lg"
        // Prevent Escape + outside clicks so the user must choose
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-bronze" />
            <DialogTitle>
              {lbl('Hemos actualizado nuestra politica de privacidad', 'We updated our privacy policy')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {lbl(
              `La version actual es ${user.privacy_current_version}. Usted acepto la version ${user.privacy_version_accepted || '(ninguna)'}. Por favor revise los cambios y acepte para continuar usando ERP Legal.`,
              `Current version is ${user.privacy_current_version}. You accepted version ${user.privacy_version_accepted || '(none)'}. Please review the changes and accept to keep using ERP Legal.`
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-bronze/20 bg-bronze/5 p-4 text-sm flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-bronze shrink-0 mt-0.5" />
          <p className="text-muted-foreground">
            {lbl(
              'Si no acepta, no podra acceder a la plataforma. Puede cerrar sesion y decidir mas tarde.',
              `If you do not accept, you won't be able to access the platform. You can sign out and decide later.`
            )}
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 accent-bronze"
          />
          <span>
            {lbl('He leido y acepto la ', 'I have read and accept the ')}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-bronze underline"
            >
              {lbl('Politica de Privacidad', 'Privacy Policy')} v{user.privacy_current_version}
            </Link>
            .
          </span>
        </label>

        <div className="flex justify-between gap-2 pt-2">
          <Button variant="ghost" onClick={handleLogout} disabled={saving}>
            {lbl('Cerrar sesion', 'Sign out')}
          </Button>
          <Button onClick={submit} className="btn-primary" disabled={!accepted || saving}>
            {saving
              ? lbl('Guardando...', 'Saving...')
              : lbl('Aceptar y continuar', 'Accept and continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
